import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Instagram, Star, MessageCircle, Globe } from "lucide-react";
import { DOCTOR, CLINICS } from "@/data/content";

const ACCENT = "text-[#8fbce8]";

const COLS = [
  {
    title: "Treatments",
    links: [
      { label: "Endoscopic Spine Surgery", to: "/endoscopic-spine-surgery" },
      { label: "Endoscopic Discectomy", to: "/endoscopic-discectomy" },
      { label: "Endoscopic Decompression", to: "/endoscopic-decompression" },
      { label: "Cervical Artificial Disc Replacement", to: "/cervical-artificial-disc-replacement" },
      { label: "Microscopic Spine Surgery", to: "/microscopic-spine-surgery" },
      { label: "Revision Spine Surgery", to: "/revision-spine-surgery" },
      { label: "Spine Surgery Cost", to: "/spine-surgery-cost-hyderabad" },
      { label: "Brain Tumour Surgery", to: "/brain-tumour-surgery" },
      { label: "Pituitary Tumour Surgery", to: "/pituitary-tumour-surgery" },
      { label: "Cerebrovascular & Skull Base Surgery", to: "/cerebrovascular-skull-base-surgery" },
      { label: "Trigeminal Neuralgia Treatment", to: "/trigeminal-neuralgia-treatment-hyderabad" },
      { label: "Head & Spine Injury", to: "/head-spine-injury-treatment-hyderabad" },
    ],
  },
  {
    title: "Conditions",
    links: [
      { label: "Slipped Disc", to: "/slipped-disc-treatment" },
      { label: "Sciatica", to: "/sciatica-treatment" },
      { label: "Lumbar Canal Stenosis", to: "/lumbar-canal-stenosis-treatment" },
      { label: "Cervical Disc Disease", to: "/cervical-disc-disease-treatment" },
      { label: "Neck Pain", to: "/neck-pain-treatment" },
      { label: "Back Pain", to: "/back-pain-treatment-hyderabad" },
      { label: "Spondylolisthesis", to: "/spondylolisthesis-treatment" },
      { label: "Spinal Tumours", to: "/spinal-tumour-treatment" },
      { label: "Foot Drop", to: "/foot-drop-treatment" },
    ],
  },
  {
    title: "Patient Resources",
    links: [
      { label: "About Dr Arun Reddy", to: "/#about" },
      { label: "Neurosurgery — Brain & Neuro Care", to: "/neurosurgery-hyderabad" },
      { label: "Best Spine Surgeon in Hyderabad", to: "/best-spine-surgeon-hyderabad" },
      { label: "Best Neurosurgeon in Hyderabad", to: "/best-neurosurgeon-hyderabad" },
      { label: "Spine Surgeon in Gachibowli", to: "/spine-surgeon-gachibowli" },
      { label: "Neurosurgeon in Madinaguda", to: "/neurosurgeon-madinaguda" },
      { label: "Neurologist or Neurosurgeon?", to: "/neurologist-vs-neurosurgeon" },
      { label: "Patient Education", to: "/#education" },
      { label: "Blog", to: "/blog" },
      { label: "FAQ", to: "/#faq" },
      { label: "Contact", to: "/#contact" },
    ],
  },
];

const ClinicList = () => (
  <div className="mt-6 space-y-5 text-sm">
    {CLINICS.map((c, i) => (
      <div key={c.name} className="flex items-start gap-3 text-white/70" data-testid={`footer-clinic-${i}`}>
        <MapPin className={`w-4 h-4 ${ACCENT} mt-0.5 shrink-0`} />
        <div>
          <div className="text-white font-semibold">
            {c.name}{" "}
            <span className={`text-[10px] uppercase tracking-wider ${ACCENT} font-bold ml-1`}>{c.tag}</span>
          </div>
          <div>{c.area}</div>
          <div className="flex items-center gap-2 mt-1">
            <Clock className={`w-3.5 h-3.5 ${ACCENT} shrink-0`} />
            <span>{c.timing}</span>
          </div>
          {c.sunday && <div className="text-white/50 text-xs mt-0.5">{c.sunday}</div>}
          <a
            href={`tel:${c.phoneRaw}`}
            className="flex items-center gap-2 mt-1 hover:text-white transition-colors"
            data-testid={`footer-clinic-phone-${i}`}
          >
            <Phone className={`w-3.5 h-3.5 ${ACCENT} shrink-0`} />
            {c.phone}
          </a>
        </div>
      </div>
    ))}
  </div>
);

const SOCIALS = [
  { Icon: Instagram, href: DOCTOR.instagram, label: "Instagram" },
  { Icon: Star, href: DOCTOR.reviewsUrl, label: "Google Reviews" },
  { Icon: MessageCircle, href: `https://wa.me/${DOCTOR.whatsapp}`, label: "WhatsApp" },
  { Icon: Globe, href: "https://www.indineurocare.com/", label: "Clinic Website" },
];

const SocialLinks = () => (
  <div className="flex items-center gap-3 mt-6">
    {SOCIALS.map(({ Icon, href, label }, i) => (
      <a
        key={label}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid={`social-link-${i}`}
        className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-white/50 hover:bg-white/10 transition-colors"
      >
        <Icon className="w-4 h-4" />
      </a>
    ))}
  </div>
);

const MapsGrid = () => (
  <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
    {CLINICS.map((c) => (
      <div key={c.name} className="rounded-2xl overflow-hidden border border-white/10">
        <div className="px-4 py-2.5 bg-white/5 text-white/80 text-xs font-semibold uppercase tracking-wider">
          {c.name} — {c.area}
        </div>
        <iframe
          title={`${c.name} location`}
          src={c.mapEmbed}
          width="100%"
          height="240"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full"
          style={{ border: 0 }}
        />
      </div>
    ))}
  </div>
);

const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-[#0b2c4d] border-t border-white/10 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-12">
          {/* Brand + hospital */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
                <span className="font-heading font-extrabold text-white text-lg">A</span>
              </div>
              <div>
                <div className="font-heading font-extrabold text-white text-[15px]">Dr. Arun Reddy M</div>
                <div className={`text-[10px] uppercase tracking-[0.2em] ${ACCENT}`}>Endoscopic Spine Surgery</div>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              Neurosurgeon and endoscopic spine surgeon offering personalised, evidence-based and
              minimally invasive spine care.
            </p>

            <ClinicList />

            <SocialLinks />
          </div>

          {/* Link columns */}
          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading font-extrabold text-white text-sm uppercase tracking-wider mb-5">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/60 hover:text-white text-sm transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <MapsGrid />

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs">
            © {new Date().getFullYear()} Dr. Arun Reddy M · CARE Hospitals, Gachibowli. All rights reserved.
          </p>
          <p className="text-white/40 text-xs max-w-xl md:text-right">
            This website is for informational purposes and does not replace professional medical advice.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
